import { useCallback, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ColumnFiltersState } from "@tanstack/react-table"
import Table from "../../table"
import ManageDreamRatePanel from "../form/manage-panel"
import DreamRateEmptyState from "../empty-state"
import NoResultsInRangeMessage from "../../activity/no-results-in-range-message"
import DateFilter, {
  DateFilterHandle,
  DateRangePreset
} from "../../activity/date-filter"
import DreamRateFilters from "../filters"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import { selectUserDetails } from "../../../../redux/slices/auth"
import { setVisiblePanel } from "../../../../redux/slices/app"
import {
  selectTabRange,
  updateRange
} from "../../../../redux/slices/engagements"
import {
  useFetchPendingSubscriptionByIdQuery,
  useFetchPendingSubscriptionsQuery
} from "../../../../redux/queries"
import {
  BBColumnDef,
  EngagementsTab,
  PendingSubscriptionFull
} from "../../../../types"
import OwnerCell from "./cells/owner"
import EngagementsCell from "./cells/engagements"
import { Pencil } from "lucide-react"
import { LinkBtn } from "../../buttons"
import ScreenReaderOnly from "../../screen-reader-only"
import { dreamRateFilter } from "../filters/dream-rate-filter"
import { getPendingSubscriptionStatus } from "../../../utils/dream-rate"
import { checkIsAdmin, checkIsSuperAdmin } from "../../../utils/user"
import { dateFilterToTimestamps } from "../../../utils/datetime"
import DreamRateUsageBadge from "../dream-rate-usage-badge"

interface Props {
  onOpenHowItWorks: () => void
}

const DreamRateTable = ({ onOpenHowItWorks }: Props) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<
    number | undefined
  >(undefined)

  const userDetails = useAppSelector(selectUserDetails)
  const loid = userDetails?.id
  const orgId = userDetails?.org?.id
  const dateFilterRef = useRef<DateFilterHandle>(null)
  const isAdmin = useMemo(
    () => checkIsAdmin(userDetails?.roles ?? []),
    [userDetails?.roles]
  )
  const isSuperAdmin = useMemo(
    () => checkIsSuperAdmin(userDetails?.roles ?? []),
    [userDetails?.roles]
  )

  const tabKey = EngagementsTab.dreamRate
  const dateFilter = useAppSelector(state => selectTabRange(state, tabKey))

  const handleDateRangeChange = useCallback(
    (range: DateRangePreset, customStartDate?: Date, customEndDate?: Date) => {
      dispatch(
        updateRange({
          tabKey,
          range,
          customStartDate: customStartDate || null,
          customEndDate: customEndDate || null
        })
      )
    },
    [dispatch, tabKey]
  )

  const handleClearDateFilter = useCallback(() => {
    dispatch(
      updateRange({
        tabKey,
        range: "all",
        customStartDate: null,
        customEndDate: null
      })
    )
  }, [dispatch, tabKey])

  const handleViewLast12Months = useCallback(() => {
    const now = new Date()
    const lastYear = new Date(now)
    lastYear.setDate(now.getDate() - 365)
    lastYear.setHours(0, 0, 0, 0)
    dispatch(
      updateRange({
        tabKey,
        range: "custom",
        customStartDate: lastYear,
        customEndDate: now
      })
    )
  }, [dispatch, tabKey])

  // Calculate timestamps from date range
  const { min: timestamp_min, max: timestamp_max } = useMemo(
    () => dateFilterToTimestamps(dateFilter),
    [dateFilter]
  )

  const { data: selectedSubscription } = useFetchPendingSubscriptionByIdQuery(
    { id: selectedSubscriptionId! },
    { skip: !selectedSubscriptionId }
  )

  // Note: Backend supports min_created and max_created per documentation
  const { data: allRows = [], isFetching } = useFetchPendingSubscriptionsQuery({
    limit: 10000,
    offset: 0,
    ...(timestamp_min !== undefined && timestamp_max !== undefined
      ? { min_created: timestamp_min, max_created: timestamp_max }
      : {}),
    ...(isSuperAdmin ? {} : isAdmin ? { org_id: orgId } : { loid })
  })

  // Use filtered results directly from API
  const rows = useMemo(() => allRows, [allRows])

  // All-time existence check (limit 1) — only when current range is empty
  const isRangeEmpty = !isFetching && rows.length === 0
  const { data: anyHistoricalRows = [], isFetching: isFetchingAllTime } =
    useFetchPendingSubscriptionsQuery(
      {
        limit: 1,
        offset: 0,
        ...(isSuperAdmin ? {} : isAdmin ? { org_id: orgId } : { loid })
      },
      { skip: !isRangeEmpty }
    )
  const hasAnyHistorical = (anyHistoricalRows?.length ?? 0) > 0
  const isCheckingHistorical = isRangeEmpty && isFetchingAllTime

  // Show onboarding empty state only when no historical data exists at all
  const shouldShowEmptyState =
    !isFetching && !isFetchingAllTime && rows.length === 0 && !hasAnyHistorical

  // Show "no results in range" message when range is empty but historical data exists
  const shouldShowRangeMessage =
    !isFetching && !isFetchingAllTime && rows.length === 0 && hasAnyHistorical
  const shouldHideHeader = !isFetching && rows.length === 0

  const navigateToDetails = useCallback(
    (row: PendingSubscriptionFull) => {
      const status = getPendingSubscriptionStatus(row)
      const subscriptionId = Number(row.subscription_id ?? 0)
      if (status === "active" && subscriptionId !== 0) {
        navigate(`/engagements/${subscriptionId}`)
      } else {
        navigate(`/dream-rate/${row.id}`)
      }
    },
    [navigate]
  )

  const columns: BBColumnDef<PendingSubscriptionFull>[] = useMemo(
    () => [
      {
        id: "subscriber",
        header: "Subscriber",
        accessorFn: row => `${row.first_name} ${row.last_name} ${row.email}`,
        cell: props => (
          <EngagementsCell
            info={props.row.original}
            onClick={navigateToDetails}
          />
        ),
        colSpan: 2,
        enableColumnFilter: true,
        filterFn: dreamRateFilter
      },
      {
        id: "owner",
        header: "Owner",
        accessorFn: row => row.loid,
        footer: props => props.column.id,
        cell: props => (
          <OwnerCell info={props.row.original} onClick={navigateToDetails} />
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "created",
        header: "",
        accessorFn: row => row.created,
        footer: props => props.column.id,
        cell: props => "",
        colSpan: 0,
        meta: { headerClassName: "hidden" }
      },
      {
        id: "edit",
        header: "",
        footer: props => props.column.id,
        enableSorting: false,
        cell: props => {
          const status = getPendingSubscriptionStatus(props.row.original)
          if (status === "active") {
            return <td></td>
          }
          return (
            <td className="text-right">
              <LinkBtn
                onClick={() => setSelectedSubscriptionId(props.row.original.id)}
                className="text-neutral-400 hover:text-primary-400"
              >
                <Pencil size={20} />
                <ScreenReaderOnly>
                  Edit subscription {props.row.original.email}
                </ScreenReaderOnly>
              </LinkBtn>
            </td>
          )
        }
      }
    ],
    [navigateToDetails]
  )

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  // Column filters state management

  const onNewRateDrop = useCallback(
    () => dispatch(setVisiblePanel("dream-rate/new")),
    [dispatch]
  )

  const resetSelectedSubscriptionId = useCallback(
    () => setSelectedSubscriptionId(undefined),
    []
  )

  const customFilterComponent = useMemo(
    () => (
      <DreamRateFilters
        columnID="subscriber"
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        className="flex items-center"
        shrinkFilter
      />
    ),
    [columnFilters]
  )

  return (
    <>
      <Table<PendingSubscriptionFull>
        className="dream-rate-table"
        isLoading={isFetching}
        data={rows}
        columns={columns}
        viewType="table"
        dropdownFilterColumnId="subscriber"
        searchPlaceholder="Search dream rate…"
        extraHeaderElement={
          <DateFilter
            ref={dateFilterRef}
            selectedRange={dateFilter.range}
            customStartDate={dateFilter.customStartDate}
            customEndDate={dateFilter.customEndDate}
            onRangeChange={handleDateRangeChange}
            onClearFilter={handleClearDateFilter}
          />
        }
        extraHeaderElementClassName="ml-auto"
        noHeader={shouldHideHeader}
        noDataElement={
          isCheckingHistorical ? (
            <td colSpan={columns.length} className="p-0">
              <div className="min-h-[500px]" />
            </td>
          ) : shouldShowEmptyState ? (
            <td colSpan={columns.length} className="p-0">
              <div className="flex items-center justify-center min-h-[500px] w-full">
                <DreamRateEmptyState
                  onLearnHowItWorks={onOpenHowItWorks}
                  onNewRateDrop={onNewRateDrop}
                />
              </div>
            </td>
          ) : shouldShowRangeMessage ? (
            <td colSpan={columns.length} className="p-0">
              <NoResultsInRangeMessage
                type="dream-rate"
                onViewLast12Months={handleViewLast12Months}
                onSelectDateRange={() => dateFilterRef.current?.openAndFocus()}
              >
                <DreamRateUsageBadge className="mb-6" />
              </NoResultsInRangeMessage>
            </td>
          ) : undefined
        }
        shrinkFilterSort
        defaultSortBy="created"
        defaultSortDir="desc"
        hasColumnFilter={true}
        customFilterFns={{}}
        customFilterComponent={customFilterComponent}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
      />

      <ManageDreamRatePanel
        open={!!(selectedSubscriptionId && selectedSubscription)}
        onClose={resetSelectedSubscriptionId}
        data={selectedSubscription}
      />
    </>
  )
}

export default DreamRateTable
